import { useQuery } from '@tanstack/react-query';
import type { Category, Menu } from '@kawai/shared';
import {
  fetchCategoriesForTenant,
  fetchMenusForTenant,
} from '../repositories/masters.js';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SegmentMasterOptions {
  menuOptions: SelectOption[];
  categoryOptions: SelectOption[];
}

const EMPTY: SegmentMasterOptions = { menuOptions: [], categoryOptions: [] };

/** メニュー: isActive 優先 → order（無ければ priceYen）→ name の順。 */
function toMenuOptions(menus: Menu[]): SelectOption[] {
  return [...menus]
    .sort((a, b) => {
      if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
      const ao = a.order ?? a.priceYen ?? 0;
      const bo = b.order ?? b.priceYen ?? 0;
      if (ao !== bo) return ao - bo;
      return (a.name ?? '').localeCompare(b.name ?? '');
    })
    .map((m) => ({
      value: m.id,
      label: m.isActive ? m.name : `${m.name}（非公開）`,
    }));
}

/** カテゴリ: isActive 優先 → order → name の順。 */
function toCategoryOptions(categories: Category[]): SelectOption[] {
  return [...categories]
    .sort((a, b) => {
      if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
      if (a.order !== b.order) return (a.order ?? 0) - (b.order ?? 0);
      return (a.name ?? '').localeCompare(b.name ?? '');
    })
    .map((c) => ({
      value: c.id,
      label: c.isActive ? c.name : `${c.name}（非公開）`,
    }));
}

/**
 * M-37: SegmentFilterBar の「予約済みメニュー / カテゴリ」select 用 option を取得する。
 *
 * cache key: `['promo', 'segmentMasters', tenantId]`。tenantId 未確定の間は disabled。
 * メニュー・カテゴリを 1 回だけ取得して option 化（セグメント条件は client-side 評価のため
 * マスタも client で保持）。失敗時は空配列（呼出側はテキスト入力フォールバックを表示）。
 */
export function useSegmentMasterOptions(
  tenantId: string | null | undefined,
): { data: SegmentMasterOptions; isLoading: boolean } {
  const q = useQuery({
    queryKey: ['promo', 'segmentMasters', tenantId],
    queryFn: async () => {
      const [menus, categories] = await Promise.all([
        fetchMenusForTenant(tenantId as string),
        fetchCategoriesForTenant(tenantId as string),
      ]);
      return {
        menuOptions: toMenuOptions(menus),
        categoryOptions: toCategoryOptions(categories),
      };
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });
  return { data: q.data ?? EMPTY, isLoading: q.isLoading };
}
